import axios, {AxiosError, AxiosResponse} from 'axios';
import queryString from 'query-string';
import {LessonObject} from './types';
import {ErrorObject, Folder, Tag} from 'types';
import {toast} from 'react-toastify';

/**
 * This function gets the lesson object by its unique id.
 * @param {LessonObject<id>} id
 * @return {Promise<LessonObject | ErrorObject>}
 */
export async function getLessonByID(id: LessonObject['id']) {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/lessons/${id}`,
  }).then((response: AxiosResponse<LessonObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

export interface GetLessonsParams {
  title?: string;
  skip?: number;
  limit?: number;
  my?: string;
  folder?: Folder;
}

/**
 * This function promises to return a list of lessons.
 * @param {GetLessonsParams} params
 * @return {Promise<LessonObject[] | ErrorObject | undefined>}
 */
export async function getLessons(params?: GetLessonsParams) {
  return axios({
    method: 'GET',
    url: queryString.stringifyUrl({
      url: `${process.env.REACT_APP_API_BASE_URL}/lessons`,
      query: params && {
        ...params,
      },
    }),
  }).then((response: AxiosResponse<LessonObject[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Create the lesson
 * @param {Omit<LessonObject, 'id'>} lesson
 * @return {Promise<LessonObject | ErrorObject>}
 */
export async function createLesson(lesson: Omit<LessonObject, 'id'>) {
  return axios({
    method: 'POST',
    url: `${process.env.REACT_APP_API_BASE_URL}/lessons`,
    data: JSON.stringify(lesson),
  }).then((response: AxiosResponse<LessonObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Edit the lesson
 * @param {LessonObject} lesson
 * @return {Promise<LessonObject | ErrorObject>}
 */
export async function editLesson(lesson: LessonObject) {
  return axios({
    method: 'PATCH',
    url: `${process.env.REACT_APP_API_BASE_URL}/lessons`,
    data: JSON.stringify(lesson),
  }).then((response: AxiosResponse<LessonObject>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Delete the lesson
 * @param {LessonObject<id>} id
 * @return {Promise<LessonObject | ErrorObject>}
 */
export async function deleteLesson(id: LessonObject['id']) {
  return axios({
    method: 'DELETE',
    url: `${process.env.REACT_APP_API_BASE_URL}/lessons/${id}`,
    data: JSON.stringify({
      id,
    }),
  }).then((response: AxiosResponse<LessonObject>) => {
    toast.success('Урок удален!');
    return response.data;
  }).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get tags
 * @return {Promise<Tag[] | ErrorObject | undefined>}
 */
export async function getTags() {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/lessons/tags`,
  }).then((response: AxiosResponse<Tag[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}

/**
 * Get folders
 * @return {Promise<Folder[] | ErrorObject | undefined>}
 */
export async function getFolders() {
  return axios({
    method: 'GET',
    url: `${process.env.REACT_APP_API_BASE_URL}/lessons/folders`,
  }).then((response: AxiosResponse<Folder[]>) => (
    response.data
  )).catch((error: AxiosError<ErrorObject>) => (
    error.response?.data
  ));
}
